// Toast/Notification Store
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useToastStore = defineStore('toast', () => {
  // State
  const toasts = ref([])
  const maxToasts = ref(5)
  const defaultTimeout = ref(5000)

  // Getters
  const activeToasts = computed(() => toasts.value)
  const toastCount = computed(() => toasts.value.length)

  // Actions
  const addToast = (toast) => {
    const id = Date.now() + Math.random()
    
    const newToast = {
      id,
      type: toast.type || 'info',
      title: toast.title || '',
      message: toast.message || '',
      timeout: toast.timeout ?? defaultTimeout.value,
      persistent: toast.persistent || false,
      actions: toast.actions || [],
      createdAt: new Date()
    }

    // Add to beginning of array
    toasts.value.unshift(newToast)

    // Remove excess toasts
    if (toasts.value.length > maxToasts.value) {
      toasts.value.splice(maxToasts.value)
    }

    // Auto-remove after timeout (unless persistent)
    if (!newToast.persistent && newToast.timeout > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.timeout)
    }

    return id
  }

  const removeToast = (id) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const clearAllToasts = () => {
    toasts.value = []
  }

  const updateToast = (id, updates) => {
    const toast = toasts.value.find(t => t.id === id)
    if (toast) {
      Object.assign(toast, updates)
    }
  }

  // Convenience methods for different toast types
  const success = (message, options = {}) => {
    return addToast({
      type: 'success',
      title: options.title || 'Sucesso',
      message,
      ...options
    })
  }

  const error = (message, options = {}) => {
    return addToast({
      type: 'error',
      title: options.title || 'Erro',
      message,
      timeout: options.timeout ?? 8000, // Longer timeout for errors
      ...options
    })
  }

  const warning = (message, options = {}) => {
    return addToast({
      type: 'warning',
      title: options.title || 'Atenção',
      message,
      ...options
    })
  }

  const info = (message, options = {}) => {
    return addToast({
      type: 'info',
      title: options.title || 'Informação',
      message,
      ...options
    })
  }

  const loading = (message, options = {}) => {
    return addToast({
      type: 'loading',
      title: options.title || 'Carregando...',
      message,
      persistent: true, // Loading toasts are persistent by default
      ...options
    })
  }

  // Promise helpers
  const promise = async (promiseArg, messages = {}) => {
    const loadingId = loading(messages.loading || 'Processando...')
    
    try {
      const result = await promiseArg
      removeToast(loadingId)
      success(messages.success || 'Operação concluída com sucesso')
      return result
    } catch (err) {
      removeToast(loadingId)
      const errorMessage = err.response?.data?.message || err.message || 'Ocorreu um erro'
      error(messages.error || errorMessage)
      throw err
    }
  }

  return {
    // State
    toasts,
    maxToasts,
    defaultTimeout,

    // Getters
    activeToasts,
    toastCount,

    // Actions
    addToast,
    removeToast,
    clearAllToasts,
    updateToast,

    // Convenience methods
    success,
    error,
    warning,
    info,
    loading,
    promise
  }
})

// Alias for backward compatibility
export const useToast = useToastStore